import React from 'react';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import type { SocialNetwork } from '@xshop/shared';
import './elementSocialLinks.css';
import { named, useShopNames } from '../../commerce/shopNames';

// A store's social profiles as a row of icons, each a link opening in a new tab and named for a screen
// reader. The Social links element draws it, and so does the foot of the phone menu.

/** The glyph each network is drawn with; the three brands lucide lacks are drawn here by hand. */
const GLYPHS: Readonly<Record<SocialNetwork, (size: number) => React.ReactNode>> = {
  instagram: (size) => <Instagram size={size} strokeWidth={1.5} aria-hidden="true" />,
  facebook: (size) => <Facebook size={size} strokeWidth={1.5} aria-hidden="true" />,
  youtube: (size) => <Youtube size={size} strokeWidth={1.5} aria-hidden="true" />,
  tiktok: (size) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-1.85-2.48V9.78a5.69 5.69 0 1 0 4.94 5.62V9.01a7.33 7.33 0 0 0 4.29 1.38V7.3a4.3 4.3 0 0 1-3.23-1.48Z" /></svg>,
  x: (size) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.75 3h3.07l-6.7 7.66L22 21h-6.17l-4.83-6.32L5.47 21H2.4l7.17-8.2L2 3h6.33l4.37 5.78Zm-1.08 16.17h1.7L7.4 4.74H5.58Z" /></svg>,
  whatsapp: (size) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2a9.9 9.9 0 0 0-8.5 14.96L2 22l5.2-1.5A9.9 9.9 0 1 0 12.04 2Zm0 18.1a8.2 8.2 0 0 1-4.2-1.15l-.3-.18-3.08.9.9-3-.2-.31a8.2 8.2 0 1 1 6.88 3.74Zm4.5-6.14c-.25-.12-1.46-.72-1.69-.8-.22-.08-.39-.12-.55.12-.16.25-.63.8-.78.97-.14.16-.29.18-.53.06a6.7 6.7 0 0 1-3.32-2.9c-.25-.43.25-.4.72-1.33.08-.16.04-.3-.02-.43l-.75-1.8c-.2-.47-.4-.4-.55-.41h-.47a.9.9 0 0 0-.65.3 2.74 2.74 0 0 0-.86 2.04 4.77 4.77 0 0 0 1 2.53 10.9 10.9 0 0 0 4.18 3.7c1.56.67 2.17.73 2.95.61.47-.07 1.46-.6 1.66-1.18.2-.58.2-1.07.14-1.18-.06-.1-.22-.16-.47-.28Z" /></svg>
};

/** What each network is called out loud, since the icon alone says nothing to a screen reader. */
const NAMES: Readonly<Record<SocialNetwork, string>> = {
  instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', youtube: 'YouTube', x: 'X', whatsapp: 'WhatsApp'
};

/** What the row draws: the profiles with an address, the store they belong to, and how big the icons are. */
type SocialIconsProps = { links: ReadonlyArray<{ network: SocialNetwork; url: string }>; storeName: string; size: number; className?: string };

// Draws the profiles as a row of icon links.
export const SocialIcons: React.FC<SocialIconsProps & Omit<React.HTMLAttributes<HTMLUListElement>, 'className'>> = ({ links, storeName, size, className, ...rest }) => {
  const said = useShopNames();
  return (
    <ul {...rest} className={['xe-social', className].filter(Boolean).join(' ')}>
      {links.map(({ network, url }) => (
        <li key={network}>
          <a href={url} target="_blank" rel="noopener noreferrer" aria-label={named(said.onNetwork, { store: storeName || said.ourStore, network: NAMES[network] })}>{GLYPHS[network](size)}</a>
        </li>
      ))}
    </ul>
  );
};
